import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Checkbox,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';
import api from '../../services/api';
import { ACADEMIC_YEARS, PARENT_CHILDREN } from '../../config/Config';
import { filterPublished } from '../../utils/publishedFilter';
import ParentLayout from '../../components/layout/ParentLayout';

function AddChildDialog({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [academicYears, setAcademicYears] = useState([]);
  const [formData, setFormData] = useState({ email: '', academic_year_id: '', grant_parent_consent: false });

  useEffect(() => {
    if (!open) return;
    setError('');
    setFormData({ email: '', academic_year_id: '', grant_parent_consent: false });
    (async () => {
      try {
        const yrRes = await api.get(ACADEMIC_YEARS + '?published_only=true');
        const yrs = Array.isArray(yrRes.data) ? yrRes.data : (yrRes.data?.results || []);
        setAcademicYears(filterPublished(yrs));
      } catch (e) {
        setAcademicYears([]);
      }
    })();
  }, [open]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    if (error) setError('');
  };

  const selectedAcademicYear = academicYears.find((y) => String(y.id) === String(formData.academic_year_id));
  const selectedGrade = selectedAcademicYear?.year ?? null;
  const requiresConsent = selectedGrade != null && Number(selectedGrade) <= 10;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requiresConsent && !formData.grant_parent_consent) {
      setError('Please confirm parent consent for grade 10 or below.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post(PARENT_CHILDREN, {
        child_email: formData.email,
        academic_year_id: formData.academic_year_id || null,
        grant_parent_consent: !!formData.grant_parent_consent,
      });
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to add child. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Add Child</DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            autoFocus
            margin="dense"
            name="email"
            label="Child email"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={handleChange}
            required
            sx={{ mb: 2 }}
          />

          <TextField
            select
            margin="dense"
            name="academic_year_id"
            label="Grade"
            fullWidth
            variant="outlined"
            value={formData.academic_year_id}
            onChange={handleChange}
            sx={{ mb: 1 }}
          >
            <MenuItem value="">
              <em>Select grade (optional)</em>
            </MenuItem>
            {academicYears.map((y) => (
              <MenuItem value={y.id} key={y.id}>
                {y.year}
              </MenuItem>
            ))}
          </TextField>

          <Typography variant="caption" color="text.secondary">
            If this child is new, we will create a pending student account, copy your family subscription, and email the invitation to them.
          </Typography>

          {requiresConsent && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Parent / Guardian Consent Required
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                This child is in grade {selectedGrade}. Before they can use the platform, we need parent or guardian consent.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                By giving consent you agree that:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 1.5, color: 'text.secondary' }}>
                <li>Your child may use this platform for tests and learning activities.</li>
                <li>Their performance data may be stored and shown to you to track progress.</li>
                <li>For paid plans, you are responsible for completing payment as prompted.</li>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    name="grant_parent_consent"
                    checked={formData.grant_parent_consent}
                    onChange={handleChange}
                  />
                }
                label="I am the parent or guardian and I give consent for this child to use Thinkstyne."
              />
              <FormHelperText sx={{ ml: 0 }}>
                We will record this consent against your email and the child account.
              </FormHelperText>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Add Child'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default function ChildrenPage() {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [removeLoadingId, setRemoveLoadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(PARENT_CHILDREN);
      setChildren(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setChildren([]);
      setError('Failed to load children.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRemove = async (relationshipId) => {
    setRemoveLoadingId(relationshipId);
    try {
      await api.delete(PARENT_CHILDREN, {
        data: { relationship_id: relationshipId },
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to remove child.');
    } finally {
      setRemoveLoadingId(null);
    }
  };

  const hasChildren = useMemo(() => children.length > 0, [children]);

  return (
    <ParentLayout>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            My Children
          </Typography>
          <Button variant="contained" onClick={() => setDialogOpen(true)}>
            Add child
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : !hasChildren ? (
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary">No children linked yet.</Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {children.map((c) => (
              <Grid item xs={12} md={6} lg={4} key={c.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography fontWeight={600}>
                      {(c.first_name || c.last_name) ? `${c.first_name || ''} ${c.last_name || ''}`.trim() : (c.email || 'Child')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {c.email}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Chip
                        size="small"
                        label={c.status === 'pending' ? 'Invitation pending' : c.status === 'declined' ? 'Invitation declined' : 'Active'}
                        color={c.status === 'active' ? 'success' : c.status === 'declined' ? 'default' : 'warning'}
                        variant={c.status === 'active' ? 'filled' : 'outlined'}
                      />
                      <Button
                        color="error"
                        size="small"
                        onClick={() => handleRemove(c.relationship_id)}
                        disabled={removeLoadingId === c.relationship_id}
                      >
                        Remove
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <AddChildDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSuccess={load}
        />
      </Box>
    </ParentLayout>
  );
}

